// hooks/use-file-upload.js — GLOBAL FILE UPLOAD HOOK
// ═══════════════════════════════════════════════════════════
// One hook for all chat features. Handles:
//   - State management (pendingFile)
//   - Image compression (max 1500px)
//   - Storage upload (save-attachment API)
//   - Artifact creation (Upload type)
//   - Post-response description update (Haiku-generated)
//
// Usage:
//   const upload = useFileUpload({ userId, supabase, sessionId });
//
//   // FileUpload onFile:
//   onFile={upload.handleFileSelect}
//
//   // In send handler:
//   const fileResult = await upload.processForSend();
//   // include fileResult.fileData in API request body
//
//   // After AI responds:
//   await upload.updateDescription(finalContent);
// ═══════════════════════════════════════════════════════════
import { useState, useRef } from 'react';
import { useArtifacts } from './use-artifacts';

export function useFileUpload({ userId, supabase, sessionId }) {
  const [pendingFile, setPendingFile] = useState(null);
  const lastUploadedUrlRef = useRef(null);
  const lastFileNameRef = useRef(null);
  const { saveArtifact } = useArtifacts({ userId, supabase });

  // ── Handle file selection from FileUpload component ──
  // Compresses images, sets pendingFile state with base64 data.
  function handleFileSelect({ base64, name, type }) {
    const isImage = type.startsWith('image/');
    const isPDF = type === 'application/pdf';

    if (isImage) {
      const img = new window.Image();
      img.onload = () => {
        const MAX = 1500;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        setPendingFile({
          base64: compressed.split(',')[1],
          name,
          type: 'image/jpeg',
          isImage: true,
          isPDF: false,
        });
      };
      img.src = base64.includes(',') ? base64 : `data:${type};base64,${base64}`;
    } else {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
      setPendingFile({ base64: cleanBase64, name, type, isImage, isPDF });
    }
  }

  // ── Clear pending file ──
  function clearPendingFile() {
    setPendingFile(null);
  }

  // ── Process file for sending ──
  // Uploads to storage, creates Upload artifact, returns data for API.
  // Returns: { fileData, publicUrl, fileToSend } or null if no file.
  async function processForSend() {
    const fileToSend = pendingFile;
    if (!fileToSend) return null;

    setPendingFile(null);

    let publicUrl = null;
    try {
      const { data: { session: authS } } = await supabase.auth.getSession();
      const attachRes = await fetch('/api/user/save-attachment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authS?.access_token || ''}`,
        },
        body: JSON.stringify({
          sessionId,
          file: {
            base64: fileToSend.base64,
            name: fileToSend.name,
            type: fileToSend.type,
            isImage: fileToSend.isImage,
          },
        }),
      });
      if (attachRes.ok) {
        const { url } = await attachRes.json();
        publicUrl = url || null;
        if (publicUrl) {
          // Create Upload artifact (fire-and-forget)
          saveArtifact(publicUrl, 'Upload', sessionId).catch(() => {});
        }
      }
    } catch (_) { /* non-critical — file upload failed, still send message */ }

    lastUploadedUrlRef.current = publicUrl;
    lastFileNameRef.current = fileToSend.name;

    return {
      fileToSend,
      publicUrl,
      fileData: {
        base64: fileToSend.base64,
        name: fileToSend.name,
        type: fileToSend.type,
        isImage: fileToSend.isImage,
        isPDF: fileToSend.isPDF,
        publicUrl,
      },
    };
  }

  // ── Update artifact description after AI responds ──
  // Uses Shift's response text to generate a meaningful title + description + bullets
  // for the Upload artifact that was created during processForSend.
  async function updateDescription(finalContent) {
    const publicUrl = lastUploadedUrlRef.current;
    const fileName = lastFileNameRef.current;
    if (!publicUrl || !finalContent || finalContent.length < 20) return;

    try {
      const { data: { session: authS } } = await supabase.auth.getSession();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authS?.access_token || ''}`,
      };

      // Generate description from Shift's response
      const descRes = await fetch('/api/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'generateArtifactDescription',
          content: finalContent.slice(0, 2000),
          title: fileName || 'Upload',
          type: 'upload',
          userId,
        }),
      });
      const descData = await descRes.json();

      if (descData.description || descData.bullets?.length || descData.title) {
        await fetch('/api/user/update-artifact-desc', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            url: publicUrl,
            title: descData.title,
            description: descData.description,
            bullets: descData.bullets,
          }),
        });
      }
    } catch (_) { /* non-critical */ }

    // Clear refs after update
    lastUploadedUrlRef.current = null;
    lastFileNameRef.current = null;
  }

  return {
    pendingFile,
    setPendingFile,
    clearPendingFile,
    handleFileSelect,
    processForSend,
    updateDescription,
  };
}
